<template>
  <main class="public-tracking" dir="rtl">
    <section class="tracking-shell">
      <header class="tracking-brand">
        <img v-if="logoUrl" :src="logoUrl" :alt="companyName" @error="onLogoError" />
        <div>
          <strong class="company-name">{{ companyName }}</strong>
          <span>رهگیری آنلاین مرسوله</span>
          <h1>آخرین وضعیت بار خود را ببینید</h1>
          <p>کد رهگیری را وارد کنید تا مسیر ثبت‌شده محموله نمایش داده شود.</p>
        </div>
      </header>

      <form v-if="showSearchForm" class="tracking-search" @submit.prevent="getTracking">
        <label for="tracking-code">کد رهگیری</label>
        <div class="search-row">
          <InputText
            id="tracking-code"
            v-model.trim="currentData.tracking_code"
            autocomplete="off"
            placeholder="مثال: HE123456"
            autofocus
          />
          <Button type="submit" label="رهگیری مرسوله" icon="pi pi-search" :loading="loading" />
        </div>
        <small>کد رهگیری روی رسید پذیرش یا پیام ارسال‌شده برای شما درج شده است.</small>
      </form>

      <section v-else class="tracking-result">
        <div class="result-toolbar">
          <Button label="رهگیری مرسوله دیگر" icon="pi pi-arrow-right" text @click="resetSearch" />
          <Tag
            v-if="orderTracking.shipment_status"
            :value="orderTracking.shipment_status.label_fa"
            :severity="statusSeverity(orderTracking.shipment_status.value)"
            :class="{ 'confirmed-status': orderTracking.shipment_status.value === 'confirmed' }"
          />
        </div>

        <div class="shipment-summary">
          <div>
            <span>کد رهگیری</span>
            <strong>{{ orderTracking.order?.order_number || '-' }}</strong>
          </div>
          <div>
            <span>فرستنده</span>
            <strong>{{ senderName }}</strong>
          </div>
          <div>
            <span>وضعیت فعلی</span>
            <strong>{{ orderTracking.shipment_status?.label_fa || 'در حال بررسی' }}</strong>
          </div>
        </div>

        <div v-if="publicTimeline.length" class="public-timeline">
          <article v-for="(entry, index) in publicTimeline" :key="entry.id || `${entry.date}-${index}`">
            <div class="timeline-marker" :class="{ active: index === 0 }">
              <i :class="index === 0 ? 'pi pi-map-marker' : 'pi pi-check'"></i>
            </div>
            <div class="timeline-card">
              <div>
                <strong>{{ entry.status_fa }}</strong>
                <time>{{ formatDate(entry.date) }}</time>
              </div>
              <p v-if="entry.description">{{ entry.description }}</p>
              <span v-if="entry.location"><i class="pi pi-map-marker"></i>{{ entry.location }}</span>
            </div>
          </article>
        </div>

        <div v-else class="empty-result">
          <i class="pi pi-clock"></i>
          <h2>هنوز رویدادی برای این مرسوله ثبت نشده است</h2>
          <p>پس از شروع عملیات حمل، وضعیت‌های قابل نمایش در این بخش قرار می‌گیرند.</p>
        </div>
      </section>
    </section>
  </main>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue';
import emitter from '@/utils/eventBus';
import { ReferenceService } from '@/service/ReferenceService';
import { useCompanyBrand } from '@/composables/useCompanyBrand';
import { formatPartyName } from '@/utils/globalHelpers';

const referenceService = new ReferenceService();
const orderTracking = ref({});
const loading = ref(false);
const showSearchForm = ref(true);
const currentData = ref({ tracking_code: '' });
const logoBroken = ref(false);
const { companyName, companyLogoUrl, loadCompanySettings } = useCompanyBrand();
const logoUrl = computed(() => (logoBroken.value ? null : companyLogoUrl.value));

const onLogoError = () => {
  logoBroken.value = true;
};

onMounted(() => {
  logoBroken.value = false;
  loadCompanySettings({ force: true });
});

const senderName = computed(() => {
  return formatPartyName(orderTracking.value.order?.sender);
});

const publicTimeline = computed(() => {
  if (orderTracking.value.shipment_timeline?.length) {
    return orderTracking.value.shipment_timeline.map((entry) => ({
      id: entry.id,
      status_fa: entry.status?.label_fa || entry.status?.label_en || 'به‌روزرسانی وضعیت',
      description: entry.description,
      date: entry.occurred_at,
      location: entry.location
    }));
  }

  return Object.entries(orderTracking.value.tracking || {})
    .flatMap(([date, entries]) =>
      entries.map((entry, index) => ({
        id: `${date}-${index}`,
        status_fa: entry.name || entry.en_name,
        description: entry.description,
        date,
        location: entry.location
      }))
    )
    .reverse();
});

const getTracking = async () => {
  if (!currentData.value.tracking_code) {
    emitter.emit('toast', { type: 'error', message: 'لطفاً کد رهگیری را وارد کنید.' });
    return;
  }

  loading.value = true;
  try {
    const response = await referenceService.getTrackingCustomer(currentData.value.tracking_code);
    orderTracking.value = response.data.data;
    showSearchForm.value = false;
  } catch (error) {
    emitter.emit('toast-axios', error);
  } finally {
    loading.value = false;
  }
};

const resetSearch = () => {
  orderTracking.value = {};
  currentData.value.tracking_code = '';
  showSearchForm.value = true;
};

const formatDate = (value) => {
  if (!value) return '-';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return new Intl.DateTimeFormat('fa-IR', {
    dateStyle: 'medium',
    timeStyle: value.includes?.('T') ? 'short' : undefined
  }).format(parsed);
};

const statusSeverity = (status) => {
  if (['confirmed', 'delivered', 'closed'].includes(status)) return 'success';
  if (['cancelled', 'delivery_failed', 'returned'].includes(status)) return 'danger';
  if (['customs_hold', 'awaiting_information'].includes(status)) return 'warning';
  return 'info';
};
</script>

<style scoped lang="scss">
.public-tracking {
  min-height: 100vh;
  padding: clamp(1rem, 4vw, 4rem);
  background: radial-gradient(
      circle at 10% 0%,
      color-mix(in srgb, var(--primary-color) 15%, transparent),
      transparent 28rem
    ),
    var(--surface-ground);
}

.tracking-shell {
  max-width: 960px;
  margin: 0 auto;
}

.tracking-brand {
  display: flex;
  align-items: center;
  gap: 1.5rem;
  margin-bottom: 2rem;
}

.tracking-brand img {
  width: 5rem;
  height: 5rem;
  object-fit: contain;
}

.tracking-brand .company-name {
  display: block;
  margin-bottom: 0.15rem;
  color: var(--text-color);
  font-size: 1.05rem;
  font-weight: 700;
}

.tracking-brand span {
  color: var(--primary-color);
  font-size: 0.8rem;
  font-weight: 700;
}

.tracking-brand h1 {
  margin: 0.25rem 0 0.5rem;
  font-size: clamp(1.6rem, 4vw, 2.5rem);
}

.tracking-brand p {
  margin: 0;
  color: var(--text-color-secondary);
}

.tracking-search,
.tracking-result {
  padding: clamp(1.25rem, 4vw, 2.5rem);
  border: 1px solid var(--surface-border);
  border-radius: 1.4rem;
  background: var(--surface-card);
  box-shadow: 0 24px 70px rgb(15 23 42 / 8%);
}

.tracking-search label {
  display: block;
  margin-bottom: 0.6rem;
  font-weight: 700;
}

.tracking-search small {
  display: block;
  margin-top: 0.7rem;
  color: var(--text-color-secondary);
}

.search-row {
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 0.75rem;
}

.search-row :deep(input) {
  width: 100%;
  min-height: 3rem;
}

.result-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1.5rem;
}

.shipment-summary {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0.75rem;
  padding: 1rem;
  margin-bottom: 2rem;
  border-radius: 1rem;
  background: var(--surface-ground);
}

.shipment-summary span,
.shipment-summary strong {
  display: block;
}

.shipment-summary span {
  margin-bottom: 0.35rem;
  color: var(--text-color-secondary);
  font-size: 0.75rem;
}

.public-timeline article {
  display: grid;
  grid-template-columns: 2.75rem 1fr;
  gap: 0.75rem;
}

.timeline-marker {
  position: relative;
  width: 2.25rem;
  height: 2.25rem;
  display: grid;
  place-items: center;
  border-radius: 50%;
  background: var(--surface-100);
  color: var(--text-color-secondary);
}

.timeline-marker::after {
  content: '';
  position: absolute;
  top: 2.4rem;
  bottom: -5rem;
  width: 1px;
  background: var(--surface-border);
}

.public-timeline article:last-child .timeline-marker::after {
  display: none;
}

.timeline-marker.active {
  background: var(--primary-color);
  color: var(--primary-color-text);
  box-shadow: 0 0 0 0.4rem color-mix(in srgb, var(--primary-color) 15%, transparent);
}

.timeline-card {
  min-width: 0;
  padding: 0.35rem 0 1.75rem;
}

.timeline-card > div {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
}

.timeline-card time,
.timeline-card span {
  color: var(--text-color-secondary);
  font-size: 0.78rem;
}

.timeline-card p {
  margin: 0.6rem 0;
  line-height: 1.7;
}

.timeline-card span {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
}

.empty-result {
  min-height: 16rem;
  display: grid;
  place-content: center;
  justify-items: center;
  text-align: center;
  color: var(--text-color-secondary);
}

.empty-result i {
  font-size: 2.5rem;
}

.empty-result h2 {
  margin: 1rem 0 0.4rem;
  color: var(--text-color);
}

.empty-result p {
  margin: 0;
}

@media (max-width: 640px) {
  .public-tracking {
    padding: 1rem;
  }

  .tracking-brand {
    align-items: flex-start;
  }

  .tracking-brand img {
    width: 3.5rem;
    height: 3.5rem;
  }

  .search-row {
    grid-template-columns: 1fr;
  }

  .shipment-summary {
    grid-template-columns: 1fr;
  }

  .timeline-card > div {
    flex-direction: column;
    gap: 0.3rem;
  }
}
</style>
